import mixins from 'vue-typed-mixins';
import { AxiosResponse } from 'axios';
import { EnumUtility, ErrorResponse } from '@codequality/vue-cli-base';

import { E入力分類, E勘定科目カテゴリ, E補助科目フラグ, Eフェーズ種別 } from '@/openapi/enum';
import { データApi, データRequest, 勘定科目Api } from '@/openapi/user';

import C保存Button from '@/components/保存Button/保存Button.vue';
import KeystrokeInputableText from '@/components/keystrokeInputableText/KeystrokeInputableText.vue';
import MoneyUnitSelect from '@/components/moneyUnitSelect/MoneyUnitSelect.vue';
import MoneyUnit決算期TableHeader from '@/components/moneyUnit決算期TableHeader/MoneyUnit決算期TableHeader.vue';
import C計画書データ表示設定CheckBox from '@/components/計画書データ表示設定CheckBox/計画書データ表示設定CheckBox.vue';
import PlbscfTableRow from '@/components/plbscfTableRow/PlbscfTableRow.vue';

import { InputableTextMixin } from '@/components/inputableText/InputableText';
import { KeystrokeInputableTextId } from '@/components/keystrokeInputableText/KeystrokeInputableText';

import AlertMixin from '@/mixins/AlertMixin';
import KeystrokeInputableTextMixin from '@/entries/user/mixins/KeystrokeInputableTextMixin';
import Plbscf要約表示Mixin from '@/entries/user/mixins/Plbscf要約表示Mixin';
import RoutesMixin from '@/entries/user/mixins/RoutesMixin';
import 計画書Mixin from '@/entries/user/mixins/計画書Mixin';
import 事業所Mixin from '@/entries/user/mixins/事業所Mixin';

import UserApiUtility from '@/classes/common/UserApiUtility';
import 実態BSService, { 実態BS, 実態BSListItem, 実態BSListValueItem } from '@/classes/service/分析/実態BSService';
import { ValueType } from '@/master/Plbscf';
import AbstractPlbscfService from '@/classes/service/AbstractPlbscfService';

enum KeystrokeInputableTextItem {
  修正事項Name,
  修正事項Value,
  中小企業特性の反映Name,
  中小企業特性の反映Value,
}

export default mixins(
  AlertMixin,
  InputableTextMixin,
  KeystrokeInputableTextMixin,
  RoutesMixin,
  計画書Mixin,
  事業所Mixin,
  Plbscf要約表示Mixin
).extend({
  name: 'P実態BS',

  components: {
    C保存Button,
    KeystrokeInputableText,
    MoneyUnitSelect,
    MoneyUnit決算期TableHeader,
    C計画書データ表示設定CheckBox,
    PlbscfTableRow,
  },

  data(): {
    readonly inputableTextItem: typeof KeystrokeInputableTextItem;
    api: {
      d: データApi;
      k: 勘定科目Api;
    };
    service: 実態BSService;
    実態BS: 実態BS | null;
    valueType: typeof ValueType;
    isEditing: boolean;
    isLoading: boolean;
  } {
    return {
      inputableTextItem: KeystrokeInputableTextItem,
      api: {
        d: UserApiUtility.ogApi(データApi),
        k: UserApiUtility.ogApi(勘定科目Api),
      },
      service: new 実態BSService(Eフェーズ種別.分析フェーズ),
      実態BS: null,
      valueType: ValueType,
      isEditing: false,
      isLoading: false,
    };
  },

  computed: {
    実態BSListItems(): 実態BSListItem[] {
      return this.filtered実態BSListItems(this.実態BS?.list);
    },
    map(): KeystrokeInputableTextId[][] {
      if (this.実態BS === null) return [];

      const map: KeystrokeInputableTextId[][] = [];
      this.実態BSListItems.forEach((listItem, i) => {
        // 読み取り専用行の場合は次行へ
        if (listItem.config.valueType === ValueType.calcValueReadonly) return;

        const ids: KeystrokeInputableTextId[] = [];
        EnumUtility.keys(KeystrokeInputableTextItem).forEach((_, j) => {
          ids.push(this.getKeystrokeInputableTextId(i, j));
        });
        map.push(ids);
      });

      return map;
    },
  },

  watch: {
    実態BS: {
      handler(newVal: 実態BS, oldVal: 実態BS): void {
        // load時は再計算せず終了
        if (this.isLoading) {
          this.isLoading = false;
          return;
        }
        if (oldVal === null || newVal === null) return;
        // 初回変更時、編集中フラグを立てる
        this.isEditing = true;
        // 再計算
        this.service.setCalcValue();
      },
      deep: true,
    },
  },

  async created(): Promise<void> {
    await this.load();
    this.setFirstKeystrokeId(this.map);
  },

  methods: {
    async load(): Promise<void> {
      // 勘定科目一覧　取得用
      const 勘定科目Options = {
        params: {
          sj: '実態BS_勘定科目',
        },
      };
      const 勘定科目Request = this.api.k.list勘定科目(
        this.事業所.pk,
        E補助科目フラグ.通常,
        E勘定科目カテゴリ.貸借,
        undefined,
        勘定科目Options
      );
      // データ一覧　取得用
      const データOptions = {
        params: {
          sj: '実態BS',
        },
      };
      const データRequest = this.api.d.listデータ(
        this.計画書.pk,
        this.service.getRelationalE入力分類s(),
        Eフェーズ種別.分析フェーズ,
        データOptions
      );
      // API呼び出し
      const responses = await Promise.all([勘定科目Request, データRequest]);
      responses;
      this.service.set勘定科目s = responses[0].data;
      this.実態BS = this.service.parse(responses[1].data);
      this.isLoading = true;
      this.isEditing = false;
    },
    async save(): Promise<void> {
      if (this.実態BS === null) return;
      // リクエスト生成
      const request: データRequest = {
        c入力分類: E入力分類.実態BS,
        c番号: 0,
        cフェーズ種別: Eフェーズ種別.分析フェーズ,
        cデータ: JSON.stringify(this.service.getCデータ実態BSItems),
      };
      // API呼び出し
      const データpk = this.実態BS.データpk;
      const response: AxiosResponse<void | ErrorResponse> =
        データpk === undefined
          ? await this.api.d.createデータ(this.計画書.pk, request)
          : await this.api.d.updateデータ(データpk!, request);
      if (this.isValidationErrorResponse(response)) return;
      window.alert('保存しました');
      // 再取得
      await this.load();
    },
    recalculate(): void {
      this.service.initializeAndSetCalcValue();
    },
    getYearValue(value: 実態BSListValueItem): number {
      return AbstractPlbscfService.getYearValue(value.plbscfYearValue);
    },
    getKeystrokeInputableTextId(index: number, item: KeystrokeInputableTextItem): KeystrokeInputableTextId {
      const id = EnumUtility.keys(KeystrokeInputableTextItem).length * index + item;
      return { id };
    },
    onChange0円非表示フラグ(): void {
      if (this.実態BS === null) return;
      this.実態BS.list = this.service.create実態BSList();
      this.isLoading = true;
      this.setFirstKeystrokeId(this.map);
    },
  },
});
